/**
 * Tier-distribution semantics — one place, because three surfaces render this data
 * and they disagreed about what it meant.
 *
 * `GetTierDistributionIncludingUnmigratedAsync` (TaskManager) returns one bucket per
 * observed subscription tier PLUS a synthetic bucket for profiles that have no
 * SubscriptionMigration row at all:
 *
 *   neverMigratedCount = totalProfiles - migratedProfiles   -> bucket key `unknown`
 *
 * That bucket is deliberate. DEC-2026-07-30 requires an unmapped/never-migrated
 * population to stay visible in an `unknown` bucket rather than silently valuing it
 * at $0 — the same reasoning behind `unpricedTiers`. Keep rendering it.
 *
 * What it is NOT is a tier, and it is NOT a subscriber. On DEV it holds 51,108
 * profiles against 2 rows in ProfileDB.UserSubscription, so summing it into a
 * subscriber count overstates subscribers by four orders of magnitude and produces
 * more subscribers than the platform has users.
 *
 * `unpricedTiers` is a different question and must not be reused for this: it means
 * "no listed price", which includes the retired-but-real Entrepreneur/Growth/Elite
 * tiers (US-17-GR-38). Those are genuine subscriptions and DO count as subscribers.
 */

/** Bucket key the API uses for profiles with no subscription record at all. */
export const UNSUBSCRIBED_TIER = 'unknown';

/** One `{ tier, count }` row — matches both OverviewTierRow and TierDistributionEntry. */
export interface TierCountRow {
  tier: string;
  count: number;
}

/** True when this row represents a real subscription tier, priced or retired. */
export function isSubscriptionTier(tier: string): boolean {
  return tier.toLowerCase() !== UNSUBSCRIBED_TIER;
}

/**
 * Splits the distribution into real subscription tiers and the never-subscribed
 * bucket, so a caller cannot accidentally total the two together.
 */
export function splitTierRows<T extends TierCountRow>(
  rows: readonly T[]
): { subscribed: T[]; unsubscribed: T[] } {
  return {
    subscribed: rows.filter((r) => isSubscriptionTier(r.tier)),
    unsubscribed: rows.filter((r) => !isSubscriptionTier(r.tier)),
  };
}

/** Profiles holding a subscription of any tier. Excludes the never-subscribed bucket. */
export function countActiveSubscribers(rows: readonly TierCountRow[]): number {
  return rows.reduce((sum, r) => (isSubscriptionTier(r.tier) ? sum + r.count : sum), 0);
}

/** Profiles with no subscription record. 0 when the API sends no such bucket. */
export function countNeverSubscribed(rows: readonly TierCountRow[]): number {
  return rows.reduce((sum, r) => (isSubscriptionTier(r.tier) ? sum : sum + r.count), 0);
}
